import { PayloadAction } from '@reduxjs/toolkit';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { iif, Observable, Observer, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import config from '../../../config';
import { MOCK } from '../../../configDebug';
import { ENROLL_AUTH_REQUEST_TYPE } from '../../models/enroll-requests';
import GlobalService from '../../services/Global.service';
import LoaderState from '../../store-util/LoaderState';
import {
  autoLogin,
  autoLoginFailed,
  autoLoginSuccess,
  checkCookieSession,
  enrollAuthCompleted,
  getCountryListSuccess,
  getIndividualExtendedInfo,
  login,
  loginFailed,
} from '../common-actions';
import { getTradingPlatforms } from '../crm';
import { processErrorInResponse, processResponse } from '../helpers';
import { RootState } from '../index';
import {
  hasValidCookie,
} from '../selectors';

import {
  AuthState,
  enrollAuth,
  enrollAuthFailed,
  loginSucceeded,
  sendNonAppropriatenessEmail,
  sendNonAppropriatenessEmailFail,
  sendNonAppropriatenessEmailSuccess,
  sendResetPassword,
  setBffClaims,
  setCookieSessionValid,
} from './index';

type AuthEpicState = Parameters<typeof hasValidCookie>[0];
const BFF_USER_CLAIM_SESSION_EXPIRES = 'bff:session_expires_in';

const getRedeemSignupTokenErrorPayload = (callName: string, error: any, action: any) => {
  const status = error?.status
    ?? error?.response?.status
    ?? error?.originalError?.response?.status
    ?? (error?.message === 'SIGNUP_TOKEN_INVALID' ? 401 : 400);

  return {
    callName,
    status,
    error,
    errorData: { action, error },
    errorStr: `[auth/epics] [call: ${callName}] ${error?.message ?? error}`,
  };
};

const loginEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(login.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    GlobalService
      .authService
      .authorize(
        action.payload.authType,
        action.payload.isPrompt,
        action.payload.isFromApp,
        action.payload.isIosDevice,
      )
      .then((authState: AuthState) => {
        console.debug('[auth/epics] Auth State - login success', authState);
        LoaderState.setLoading(true);
        observer.next(loginSucceeded({ userData: authState }));
      })
      .catch((err: any) => {
        console.error('[auth/epics] Auth State - login error', err);
        if (action.payload?.authType !== 'login') {
          observer.next(loginFailed({ errorStr: 'Login failed', errorData: err }));
        }
      });
  })),
);

const enrollAuthEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(enrollAuth.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const { requestType } = action.payload;
    const callName = `enrollAuth/${ENROLL_AUTH_REQUEST_TYPE[requestType]}`;
    const { email, phoneCode, phoneNumber } = action.payload;
    const {
      userId: stateUserId, firstName, lastName, country,
    } = state$.value.auth.enroll;
    const userId = action.payload.userId
      ?? stateUserId
      ?? state$.value.auth.enrollUserId
      ?? state$.value.crm.individualExtendedInfo?.user_correlation_id;

    switch (requestType) {
      // TODO: Remove this enum value and related code with ART-1023
      case ENROLL_AUTH_REQUEST_TYPE.RECOVER:
        return;

      case ENROLL_AUTH_REQUEST_TYPE.REGISTER: {
        const {
          password,
          confirmPassword,
          extraDetails,
          planId,
          autoPass,
          referrerClientId,
          referralCode,
        } = action.payload;

        GlobalService
          .authService
          .registerUser(
            email,
            password,
            confirmPassword,
            extraDetails,
            planId,
            autoPass,
            referrerClientId,
            referralCode,
          )
          .then((response: any) => {
            processResponse(callName, {}, response, observer, enrollAuthCompleted, enrollAuthFailed);
          })
          .catch((error: any) => processErrorInResponse(callName, error, action, observer, enrollAuthFailed));
        break;
      }

      case ENROLL_AUTH_REQUEST_TYPE.CONFIRM_EMAIL: {
        const { token, referrerClientId } = action.payload;
        GlobalService
          .authService
          .confirmEmail(email, token, referrerClientId)
          .then((response: any) => {
            processResponse(
              callName,
              {},
              response,
              observer,
              enrollAuthCompleted,
              enrollAuthFailed,
              false,
              action.payload,
            );
          })
          .catch((error: any) => processErrorInResponse(callName, error, action, observer, enrollAuthFailed));
        break;
      }

      case ENROLL_AUTH_REQUEST_TYPE.REDEEM_SIGNUP_TOKEN: {
        const {
          username,
          signupToken,
          returnUrl,
          referrerClientId,
        } = action.payload;
        GlobalService
          .authService
          .confirmEmail(username, signupToken, referrerClientId)
          .then((response: any) => {
            const isEmailConfirmed = processResponse(
              callName,
              {},
              response,
              observer,
              undefined,
              enrollAuthFailed,
              false,
              action.payload,
            );

            if (!isEmailConfirmed) return;

            GlobalService
              .authService
              .redeemSignupToken(username, signupToken, returnUrl)
              .then(() => {
                observer.next(enrollAuthCompleted({ callName, status: 204 }));
              })
              .catch((error: any) => processErrorInResponse(
                callName,
                error,
                action,
                observer,
                enrollAuthFailed,
                undefined,
                getRedeemSignupTokenErrorPayload(callName, error, action),
              ));
          })
          .catch((error: any) => processErrorInResponse(callName, error, action, observer, enrollAuthFailed));
        break;
      }

      case ENROLL_AUTH_REQUEST_TYPE.RESEND_EMAIL: {
        const {
          referrerClientId,
          referralCode,
          planId,
          autoPass,
        } = state$.value.auth.enroll;
        const selectedTradingPlanId = planId ? String(planId) : null;
        GlobalService
          .authService
          .resendEmail(
            email,
            firstName,
            lastName,
            country,
            selectedTradingPlanId,
            autoPass,
            referrerClientId,
            referralCode,
          )
          .then((response: any) => {
            processResponse(callName, {}, response, observer, enrollAuthCompleted, enrollAuthFailed);
          })
          .catch((error: any) => processErrorInResponse(callName, error, action, observer, enrollAuthFailed));
        break;
      }

      case ENROLL_AUTH_REQUEST_TYPE.SEND_PHONE_CODE:
        GlobalService
          .authService
          .sendPhoneCode(userId as number, (phoneCode + phoneNumber))
          .then((response: any) => {
            processResponse(callName, {}, response, observer, enrollAuthCompleted, enrollAuthFailed);
          })
          .catch((error: any) => processErrorInResponse(callName, error, action, observer, enrollAuthFailed));
        break;

      case ENROLL_AUTH_REQUEST_TYPE.VERIFY_PHONE_CODE:
        GlobalService
          .authService
          .verifyPhoneNumber(userId as number, action.payload.code)
          .then((response: any) => {
            processResponse(callName, {}, response, observer, enrollAuthCompleted, enrollAuthFailed);
          })
          .catch((error: any) => processErrorInResponse(callName, error, action, observer, enrollAuthFailed));
        break;

      default:
        throw new Error(`[auth/epics] Enroll auth request '${callName}' not implemented`);
    }
  })),
);

const enrollAuthCompletedEpic = (action$: Observable<any>) => action$.pipe(
  ofType(enrollAuthCompleted.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const { callName } = action.payload;
    const isVerifyPhone = callName?.split('/')[1] === ENROLL_AUTH_REQUEST_TYPE[ENROLL_AUTH_REQUEST_TYPE.VERIFY_PHONE_CODE];
    const isConfirmEmail = callName?.split('/')[1] === ENROLL_AUTH_REQUEST_TYPE[ENROLL_AUTH_REQUEST_TYPE.CONFIRM_EMAIL];
    const isRedeemSignupToken = callName?.split('/')[1]
      === ENROLL_AUTH_REQUEST_TYPE[ENROLL_AUTH_REQUEST_TYPE.REDEEM_SIGNUP_TOKEN];

    if (isVerifyPhone) {
      observer.next(getIndividualExtendedInfo({ skipCookieCheck: true }));
    } else if (isConfirmEmail || isRedeemSignupToken) {
      observer.next(getTradingPlatforms());
    }
  })),
);


const autoLoginEpic = (action$: Observable<any>, state$: StateObservable<AuthEpicState>) => action$.pipe(
  ofType(autoLogin.type),
  mergeMap(() => iif(() => hasValidCookie(state$.value), of(autoLoginSuccess()), of(autoLoginFailed()))),
);


/**
 * Triggered when auto login after sign-up is not successful
 */
const autoLoginFallbackEpic = (action$: Observable<any>) => action$.pipe(
  ofType(autoLoginFailed.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'autoLoginFallback';
    const customConfig = { additionalParameters: {} };
    GlobalService
      .authService
      .authorize(customConfig)
      .then((authState: AuthState) => {
        processResponse(
          callName,
          customConfig,
          authState,
          observer,
          loginSucceeded,
          loginFailed,
          false,
          null,
          authState,
        );
      })
      .catch((error: any) => {
        processErrorInResponse(
          callName,
          error,
          action,
          observer,
          loginFailed,
        );
      });
  })),
);




const sendResetPasswordEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(sendResetPassword.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const email = state$.value.crm.individualExtendedInfo?.email;
    GlobalService
      .authService
      .sendResetPassword(
        email || '',
        config.auth.clientId || '',
        config.auth.redirectUrlProfileSecurity,
      )
      .then(resp => {
        processResponse(
          'sendResetPassword',
          {},
          resp,
          observer,
        );
      });
  })),
);

const automaticLoginInEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getCountryListSuccess.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    if (!MOCK.ENABLED && hasValidCookie(state$.value)) {
      observer.next(loginSucceeded({}));
    }
  })),
);

const checkCookieSessionEpic = (action$: Observable<any>) => action$.pipe(
  ofType(checkCookieSession.type),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    fetch(config.auth.bffUserUrl, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF': '1',
      },
    })
      .then(response => {
        if (!response.ok) {
          observer.next(setCookieSessionValid(false));
          observer.complete();
          return undefined;
        }

        return response.json();
      })
      .then((claims: Array<{ type: string; value: any }> | undefined) => {
        if (!claims) return;

        const isValid = claims.some(claim => claim.type === BFF_USER_CLAIM_SESSION_EXPIRES);

        if (isValid) {
          observer.next(setBffClaims(claims));
        }

        observer.next(setCookieSessionValid(isValid));
        observer.complete();
      })
      .catch(() => {
        observer.next(setCookieSessionValid(false));
        observer.complete();
      });
  })),
);

const sendNonAppropriatenessEmailEpic = (
  action$: Observable<PayloadAction<string>>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(sendNonAppropriatenessEmail.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'crm/sendNonAppropriatenessEmail';

    const email = state$.value.crm.individualExtendedInfo?.email ?? '';

    GlobalService
      .authService
      .sendNonAppropriatenessEmail(email ?? '')
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          sendNonAppropriatenessEmailSuccess,
          sendNonAppropriatenessEmailFail,
        );
      }))
      .catch(error => processErrorInResponse(callName, error, action, observer, sendNonAppropriatenessEmailFail));
  })),
);


export default combineEpics(
  loginEpic,
  autoLoginEpic,
  enrollAuthEpic,
  enrollAuthCompletedEpic,
  autoLoginFallbackEpic,
  sendResetPasswordEpic,
  sendNonAppropriatenessEmailEpic,
  automaticLoginInEpic,
  checkCookieSessionEpic,
);
